import './VehicleSelect.scss';
import { Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Col, Row } from "reactstrap";
import { IOptionsRequest, IVehicleIdentity, IVehicleIdentityChoices, IVehicleSelectOptions } from "../../../models/codeweavers.model";
import config from "../../../config";
import { IFinanceCalculatorState } from '../../../models/finance-calculator.model';
import VehicleTypeToggle from './VehicleTypeToggle/VehicleTypeToggle';

const VehicleOptionSelect = ({
    className = undefined,
    desc = false,
    disabled = false,
    id = "label-option",
    incUndefined = false,
    label = "option",
    onChange = undefined,
    options = [],
    top = undefined,
    value,
}: any & {
    className?: string;
    desc?: boolean;
    disabled?: boolean;
    id: string;
    incUndefined?: boolean;
    label: string;
    onChange?: SelectChangeEvent;
    options?: IVehicleSelectOptions[];
    top?: number;
    value?: string;
}) => {
    // console.log("VehicleOptionSelect: options = " + JSON.stringify(options));

    const sortAlpha = (a: IVehicleSelectOptions, b: IVehicleSelectOptions) => {
        let textA = a.Display?.toUpperCase() || "";
        let textB = b.Display?.toUpperCase() || "";
        return textA < textB ? 1 : textA > textB ? -1 : 0;
    };

    return (
        <>
            <FormControl className="left-text-align" disabled={disabled} variant="standard" size="small">
                <InputLabel id={id}>{label}</InputLabel>
                <Select
                    displayEmpty
                    className={className}
                    labelId={id}
                    id={"select-" + id}
                    key={"key-" + id}
                    value={value}
                    onChange={onChange}
                    label={label}
                >
                    { incUndefined && (
                    <MenuItem key={"option-" + id + "-undefined"} value={undefined}></MenuItem>
                    )}
                    {options && options
                        .sort((a: IVehicleSelectOptions, b: IVehicleSelectOptions) => (desc ? sortAlpha(a, b) : sortAlpha(b, a)))
                        .slice(0, top || 9999)
                        .map((option: IVehicleSelectOptions, i: number) => (
                            <MenuItem key={"option-" + id + "-" + i} value={option.Id}>{option.Display}</MenuItem>
                        ))}
                </Select>
            </FormControl>
        </>
    );
};

const VehicleSelect = ({
    control,
    label,
    name = "amount",
    mainstate, 
    onChange = undefined,
    onEdit = undefined,
    updateMainState
}: any & {
    control?: any;
    label?: string;
    name?: string;
    mainstate: IFinanceCalculatorState, 
    onChange?: (value: any) => void;
    onEdit?: () => void;
    updateMainState: React.Dispatch<any>
}) => {
    // const [loading, setLoading] = useState(true);
    const [state, setState] = useState<IVehicleIdentity>(
        { ...mainstate.formdata.identity}
        // { year: undefined, manufacturer: undefined, model: undefined, variant: undefined, derivative: undefined/*, id: undefined*/ }
        );
    const [choices, setChoices] = useState<IVehicleIdentityChoices>({
        year: [], manufacturer: [], model: [], variant: [], derivative: []
    });
    const allChanges = false;

    const fetchCodeweaversPost = (body: IOptionsRequest) => {
        return fetch(config.REACT_APP_CW_URL + "/api/vehicleselection/datasets/CAP/options", {
            method: "post", body: JSON.stringify(body),
            headers: {
                "Accept": "*/*",
                "Content-Type": "application/json",
                "X-CW-ApiKey": config.REACT_APP_CW_KEY || ""
            }
        })
    }

    const fetchDataAsCallback = useCallback(
        (body: any) => {
            const fetchData = (body: any) => {
                // console.log("fetchData: body = " + JSON.stringify(body));
                // return fetchMockGet(body)
                return fetchCodeweaversPost(body)
                /*
                if (config.REACT_APP_CW_MOCK === "true") {
                    return fetchMockGet()
                } else {
                    return fetchCodeweaversPost(body)
                }
                */
            }

            return fetchData(body);
        },
        [],
    );

    const getManufacturerRequestPayload = (currState: IVehicleIdentity): IOptionsRequest => {
        const res = getYearRequestPayloadCallback(currState);
        // console.log("getManufacturerRequestPayload: state = " + JSON.stringify(currState));
        if (currState.year) {
            res.PreviouslySelectedOptions.push(currState.year);
        }
        // console.log("getManufacturerRequestPayload: res = " + JSON.stringify(res));
        return res;
    }

    const getModelRequestPayload = (currState: IVehicleIdentity): IOptionsRequest => {
        const res = getManufacturerRequestPayload(currState);
        // console.log("getModelRequestPayload: state = " + JSON.stringify(currState));
        if (currState.manufacturer) {
            res.PreviouslySelectedOptions.push(currState.manufacturer);
        }
        // console.log("getModelRequestPayload: res = " + JSON.stringify(res));
        return res;
    }

    const getVariantRequestPayload = (currState: IVehicleIdentity): IOptionsRequest => {
        const res = getModelRequestPayload(currState);
        // console.log("getVariantRequestPayload: state = " + JSON.stringify(currState));
        if (currState.model) {
            res.PreviouslySelectedOptions.push(currState.model);
        }
        // console.log("getVariantRequestPayload: res = " + JSON.stringify(res));
        return res;
    }

    const getDerivativeRequestPayload = (currState: IVehicleIdentity): IOptionsRequest => {
        const res = getVariantRequestPayload(currState);
        // console.log("getDerivativeRequestPayload: state = " + JSON.stringify(currState));
        if (currState.variant) {
            res.PreviouslySelectedOptions.push(currState.variant);
        }
        // console.log("getDerivativeRequestPayload: res = " + JSON.stringify(res));
        return res;
    }

    const onTypeChanged = (event: object, value: any) => {
        // console.log("onTypeChanged: ");
        if (allChanges || !state.type || (state.type !== value)) {
            setState((prevState) => {
                const newState = { ...prevState, type: value, year: undefined, manufacturer: undefined, model: undefined, variant: undefined, derivative: undefined, vehicleDesc: undefined };

                fetchDataAsCallback(getYearRequestPayloadCallback(newState))
                    .then((res) => res.json())
                    .then((res) => {
                        // console.log("onTypeChanged Post: " + JSON.stringify(res));
                        if (res && res.Options) {
                            setChoices({ ...choices, year: res.Options/*, manufacturer: [], model: [], variant: [], derivative: []*/ });
                        }
                    })
                    .catch((err) => console.error("onTypeChanged error: " + JSON.stringify(err)));

                return newState;
            });
        }
    }

    const onYearChanged = (event: SelectChangeEvent) => {
        // console.log("onYearChanged changed to " + JSON.stringify(event.target.value));
        if (allChanges || !state.year || (state.year !== event.target.value)) {
            setState((prevState) => {
                // const prevValue = state.manufacturer || undefined;
                let newState = { ...prevState, year: event.target.value, manufacturer: undefined, model: undefined, variant: undefined, derivative: undefined, vehicleDesc: undefined };

                fetchDataAsCallback(getManufacturerRequestPayload(newState))
                    .then((res) => res.json())
                    .then((res) => {
                        // console.log("onYearChanged Post: " + JSON.stringify(res));
                        if (res && res.Options) {
                            setChoices({ ...choices, manufacturer: res.Options/*, model: [], variant: [], derivative: []*/});
                            /*
                            const f = res.Options.find((x: IVehicleSelectOptions) => x.Id === prevValue);
                            if (f) {
                                newState = {...newState, manufacturer: prevValue};
                            }
                            */
                        }
                    })
                    .catch((err) => console.error("onYearChanged error: " + JSON.stringify(err)));

                return newState;
            });
        }
    }

    const onManufacturerChanged = (event: SelectChangeEvent) => {
        // console.log("onManufacturerChanged changed to " + JSON.stringify(event.target.value));
        if (allChanges || !state.manufacturer || (state.manufacturer !== event.target.value)) {

            setState((prevState) => {
                const newState = { ...prevState, manufacturer: event.target.value, model: undefined, variant: undefined, derivative: undefined, vehicleDesc: undefined };

                fetchDataAsCallback(getModelRequestPayload(newState))
                .then((res) => res.json())
                .then((res) => {
                    // console.log("onManufacturerChanged Post: " + JSON.stringify(res));
                    if (res && res.Options) {
                        setChoices({ ...choices, model: res.Options/*, variant: [], derivative: []*/ });
                    }
                })
                .catch((err) => console.error("onManufacturerChanged error: " + JSON.stringify(err)));

                return newState;
            });
        }
    }

    const onModelChanged = (event: SelectChangeEvent) => {
        // console.log("onModelChanged changed to " + JSON.stringify(event.target.value));
        if (allChanges || !state.model || (state.model !== event.target.value)) {

            setState((prevState) => {
                const newState = { ...prevState, model: event.target.value, variant: undefined, derivative: undefined, vehicleDesc: undefined };

                fetchDataAsCallback(getVariantRequestPayload(newState))
                .then((res) => res.json())
                .then((res) => {
                    // console.log("onModelChanged Post: " + JSON.stringify(res));
                    if (res && res.Options) {
                        setChoices({ ...choices, variant: res.Options/*, derivative: []*/ });
                    }
                })
                .catch((err) => console.error("onModelChanged error: " + JSON.stringify(err)));

                return newState;
            });
        }
    }

    const onVariantChanged = (event: SelectChangeEvent) => {
        // console.log("onVariantChanged changed to " + JSON.stringify(event.target.value));
        if (allChanges || !state.variant || (state.variant !== event.target.value)) {

            setState((prevState) => {
                const newState = { ...prevState, variant: event.target.value, derivative: undefined, vehicleDesc: undefined };

                fetchDataAsCallback(getDerivativeRequestPayload(newState))
                .then((res) => res.json())
                .then((res) => {
                    // console.log("onVariantChanged Post: " + JSON.stringify(res));
                    if (res && res.Options) {
                        setChoices({ ...choices, derivative: res.Options });
                    }
                })
                .catch((err) => console.error("onVariantChanged error: " + JSON.stringify(err)));

                return newState;
            });
        }
    }

    const onDerivativeChanged = (event: SelectChangeEvent) => {
        setState((prevState) => {
            const newState = { ...prevState, derivative: event.target.value };
            const fullDesc = getFullVehicleDescription(newState);
            const x = { ...mainstate, formdata: {...mainstate.formdata, identity: { ...newState, vehicleDesc: fullDesc }/*, vehicle: event.target.value, vehicleDesc: fullDesc, vehicleYear: newState.year*/} };
            // console.log("onDerivativeChanged: " + JSON.stringify(x));
            if (onChange) {
                onChange(event.target.value);
            }
            if (updateMainState) {
                updateMainState(x);
            }
            return newState;
        });
    }

    const getFullVehicleDescription = (currState: IVehicleIdentity) => {
        let res = "";
        if (currState.manufacturer) {
            res = currState.manufacturer;
            if (currState.model) {
                res = res + " " + currState.model;
                if (currState.variant) {
                    res = res + " " + currState.variant;
                    if (currState.derivative) {
                        const f = choices.derivative.find((x: IVehicleSelectOptions) => x.Id === currState.derivative);
                        if (f) {
                            res = res + " " + f.Display;
                        }
                    }
                }
            }
        }
        return res;
    }

    const onEditVehicle = () => {
        setState((prevState) => {
            const newState = { ...prevState, derivative: undefined };
            // console.log("onEditVehicle: " + JSON.stringify(newState));
            if (onEdit) {
                onEdit();
            }
            return newState;
        });
    }

    const getYearRequestPayloadCallback = useCallback(
        (currState: any) => {
                return { PreviouslySelectedOptions: [currState.type || "Car"], ConfigurableOnly: false };
        },
        [],
    );

    useEffect(() => {
        // console.log("VehicleSelect.useEffect");
        if (choices.year.length === 0) {
            // fetchData(getYearRequestPayload(state))
            fetchDataAsCallback(getYearRequestPayloadCallback(state))
            .then((res) => res.json())
            .then((res) => {
                // console.log("useEffect fetchData: " + JSON.stringify(res));
                if (res && res.Options) {
                    setChoices({ ...choices, year: res.Options });
                }
            });
        }
    }, [choices, fetchDataAsCallback, getYearRequestPayloadCallback, state]);

    return (
        <>
            <Controller
                key="amount-controller"
                name={name}
                control={control}
                render={({
                    field: { onChange, value },
                    fieldState: { error },
                    formState,
                }) => (
                    <>
                        <Card>
                            <CardContent className={!state.derivative ? "shown" : "hidden"}>
                                <Row>
                                    <Col className="center-text-align margin-bottom" sm={12}>
                                        <VehicleTypeToggle
                                            className="vtype"
                                            id={"select-type"}
                                            label={"Type"}
                                            onChange={onTypeChanged} 
                                            value={state.type || "Car"}/>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="left-text-align" sm={12} md={3}>
                                        <VehicleOptionSelect
                                            className="year"
                                            desc={true}
                                            top={15}
                                            id={"select-year"}
                                            label={"Year"}
                                            options={choices.year}
                                            onChange={onYearChanged} 
                                            value={state.year} />
                                    </Col>
                                    <Col className="left-text-align" sm={12} md={4}>
                                        <VehicleOptionSelect
                                            className="manufacturer"
                                            id={"select-manufacturer"}
                                            label={"Manufacturer"}
                                            options={choices.manufacturer}
                                            onChange={onManufacturerChanged}
                                            disabled={!state.year}
                                            value={state.manufacturer}  />
                                    </Col>
                                    <Col className="left-text-align" sm={12} md={5}>
                                        <VehicleOptionSelect
                                            className="model"
                                            id={"select-model"}
                                            label={"Model"}
                                            options={choices.model}
                                            onChange={onModelChanged}
                                            disabled={!state.manufacturer}
                                            value={state.model}  />
                                    </Col>
                                    <Col className="left-text-align" sm={12} md={5}>
                                        <VehicleOptionSelect
                                            className="variant"
                                            id={"select-variant"}
                                            label={"Variant"}
                                            options={choices.variant}
                                            onChange={onVariantChanged}
                                            disabled={!state.model}
                                            value={state.variant}  />
                                    </Col>
                                { !state.derivative &&(
                                    <Col className="left-text-align" sm={12} md={7}>
                                        <VehicleOptionSelect
                                            className="derivative"
                                            id={"select-derivative"}
                                            label={"Derivative"}
                                            options={choices.derivative}
                                            onChange={onDerivativeChanged}
                                            disabled={!state.variant}/>
                                    </Col>
                                )}
                                </Row>
                            </CardContent>
                            <CardContent className={state.derivative ? "shown" : "hidden"}>
                                <Row>
                                    <Col className="left-text-align">
                                        <small>{getFullVehicleDescription(state)}</small>&nbsp;
                                        <Button type="button" size="small" color="primary" onClick={onEditVehicle}>
                                            <Tooltip title="Edit Vehicle" placement="right">
                                                <EditIcon />
                                            </Tooltip>
                                        </Button>
                                    </Col>
                                </Row>
                            </CardContent>
                        </Card>
                    </>

                )}
            />
        </>
    );
};

export default VehicleSelect;
